import { useMemo, useState } from "react";
import CitationPreviewModal from "./CitationPreviewModal";

interface Props {
  html: string;
  /** Konteks pratinjau dokumen: "public" (tanpa auth) atau "admin" (blob+Bearer). */
  variant: "public" | "admin";
  /** Kelas tambahan untuk wrapper prose. */
  className?: string;
}

interface Target {
  docId: string;
  page: number;
}

interface Reference {
  num: number;
  docId: string;
  /** Label pendek ramah-awam (mis. "UU No. 9 Tahun 2018"), dari backend. */
  label: string;
  judul: string;
  nomor: string;
  pages: number[];
}

interface Hover {
  num: number;
  label: string;
  judul: string;
  nomor: string;
  page: number;
  /** Posisi (viewport) marker untuk menempatkan tooltip. */
  x: number;
  y: number;
}

// Marker sitasi `[n]` (superscript biru) yang dapat diklik. Konten lama tanpa
// `data-doc-id` tetap teks biasa. `data-cite-num` ditanam saat transform.
const MARKER_STYLE =
  "[&_[data-cite-num]]:align-super [&_[data-cite-num]]:ml-0.5 [&_[data-cite-num]]:text-[0.7em] " +
  "[&_[data-cite-num]]:font-semibold [&_[data-cite-num]]:text-blue-600 [&_[data-cite-num]]:no-underline " +
  "[&_[data-cite-num]]:cursor-pointer hover:[&_[data-cite-num]]:text-blue-800";

// Catatan: plugin `@tailwindcss/typography` tidak terpasang, jadi kelas `prose`
// tidak berefek — preflight Tailwind malah menghapus marker <ul>/<ol>. Karena itu
// tipografi (paragraf, daftar, marker) di-styling eksplisit lewat varian [&_…].
const BASE_PROSE =
  "max-w-none text-sm text-slate-700 break-words leading-relaxed " +
  "[&_h1]:text-base [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-1 " +
  "[&_h2]:text-sm [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-1 " +
  "[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-0.5 " +
  "[&_p]:text-sm [&_p]:my-2.5 " +
  "[&_strong]:font-semibold [&_strong]:text-slate-900 " +
  "[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:space-y-1 " +
  "[&_ol]:my-2 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:space-y-1 " +
  "[&_li]:text-sm [&_li]:pl-1 [&_li]:marker:text-slate-400 " +
  // Tabel: garis, header abu-abu, padding sel — agar tampil seperti di dokumen.
  "[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_table]:block [&_table]:overflow-x-auto " +
  "[&_thead]:bg-slate-100 " +
  "[&_th]:border [&_th]:border-slate-300 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-slate-900 " +
  "[&_td]:border [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-1.5 [&_td]:align-top " +
  "[&_:first-child]:mt-0 [&_:last-child]:mb-0";

/** Judul utama yang ditampilkan: label pendek → judul → "dok. <nomor>" → "Dokumen". */
function primaryLabel(label: string, judul: string, nomor: string): string {
  return label || judul || (nomor ? `dok. ${nomor}` : "Dokumen");
}

/** Gabungkan halaman berurutan jadi rentang: [3,15,18,19,24] → "3, 15, 18–19, 24". */
function formatPages(pages: number[]): string {
  const parts: string[] = [];
  for (let i = 0; i < pages.length; ) {
    let j = i;
    while (j + 1 < pages.length && pages[j + 1] === pages[j] + 1) j++;
    parts.push(j > i ? `${pages[i]}–${pages[j]}` : `${pages[i]}`);
    i = j + 1;
  }
  return parts.join(", ");
}

/**
 * Ubah HTML beranotasi backend menjadi HTML dengan marker `[n]` (penomoran PER
 * DOKUMEN — dokumen yang sama memakai nomor yang sama, halaman dikumpulkan), dan
 * kumpulkan daftar referensi. Atribut `data-doc-id`/`data-page`/`data-doc-judul`/
 * `data-doc-nomor` dipertahankan untuk klik (modal) & tooltip (hover).
 */
function transform(html: string): { html: string; refs: Reference[] } {
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return { html, refs: [] };
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const numByDoc = new Map<string, number>();
  const refByDoc = new Map<string, Reference>();

  doc.querySelectorAll<HTMLElement>("[data-doc-id]").forEach((el) => {
    const docId = el.getAttribute("data-doc-id") || "";
    if (!docId) return;
    const page = Number(el.getAttribute("data-page")) || 1;
    const label = el.getAttribute("data-doc-label") || "";
    const judul = el.getAttribute("data-doc-judul") || "";
    const nomor = el.getAttribute("data-doc-nomor") || "";

    let num = numByDoc.get(docId);
    if (num === undefined) {
      num = numByDoc.size + 1;
      numByDoc.set(docId, num);
      refByDoc.set(docId, { num, docId, label, judul, nomor, pages: [] });
    }
    const ref = refByDoc.get(docId)!;
    if (!ref.label && label) ref.label = label;
    if (!ref.judul && judul) ref.judul = judul;
    if (!ref.nomor && nomor) ref.nomor = nomor;
    if (!ref.pages.includes(page)) ref.pages.push(page);

    // Jadikan marker ringkas; buang inline style AI agar styling marker konsisten.
    el.removeAttribute("style");
    el.setAttribute("data-cite-num", String(num));
    el.setAttribute("role", "button");
    el.setAttribute("tabindex", "0");
    el.textContent = `[${num}]`;
  });

  const refs = Array.from(refByDoc.values()).sort((a, b) => a.num - b.num);
  refs.forEach((r) => r.pages.sort((a, b) => a - b));
  return { html: doc.body.innerHTML, refs };
}

/**
 * Merender konten aspek (HTML) dengan sitasi sebagai nomor referensi `[n]`:
 * hover marker → tooltip nama dokumen + halaman; klik marker / entri daftar →
 * modal pratinjau dokumen pada halaman tersitir. Daftar referensi (dedup per
 * dokumen) ditampilkan di bawah. Sitasi tanpa `data-doc-id` (konten lama) tetap
 * tampil sebagai teks biasa.
 */
export default function CitedContent({ html, variant, className }: Props) {
  const [target, setTarget] = useState<Target | null>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const { html: rendered, refs } = useMemo(() => transform(html), [html]);
  // Metadata sitasi diagregasi per-dokumen → tooltip pakai data terkaya dari
  // SEMUA sitasi dokumen itu, bukan hanya span yang dihover (lebih konsisten
  // dengan daftar referensi; menolong saat sebagian span tak punya metadata).
  const refByDocId = useMemo(() => {
    const m = new Map<string, Reference>();
    refs.forEach((r) => m.set(r.docId, r));
    return m;
  }, [refs]);

  const open = (el: HTMLElement) => {
    const docId = el.dataset.docId;
    if (!docId) return;
    setTarget({ docId, page: Number(el.dataset.page) || 1 });
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-doc-id]");
    if (el) open(el);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-doc-id]");
    if (el) {
      e.preventDefault();
      open(el);
    }
  };

  const handleOver = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-doc-id]");
    if (!el) return;
    const ref = el.dataset.docId ? refByDocId.get(el.dataset.docId) : undefined;
    const rect = el.getBoundingClientRect();
    setHover({
      num: ref?.num || Number(el.dataset.citeNum) || 0,
      label: ref?.label || el.dataset.docLabel || "",
      judul: ref?.judul || el.dataset.docJudul || "",
      nomor: ref?.nomor || el.dataset.docNomor || "",
      page: Number(el.dataset.page) || 1, // halaman tetap per-sitasi (bukan agregat)
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleOut = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-doc-id]");
    if (!el) return;
    const related = e.relatedTarget as Node | null;
    if (related && el.contains(related)) return; // masih di dalam marker
    setHover(null);
  };

  return (
    <>
      <div
        className={`${BASE_PROSE} ${MARKER_STYLE} ${className ?? ""}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseOver={handleOver}
        onMouseOut={handleOut}
        dangerouslySetInnerHTML={{ __html: rendered }}
      />

      {refs.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-3">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Referensi
          </div>
          <ol className="space-y-2">
            {refs.map((r) => {
              const primary = primaryLabel(r.label, r.judul, r.nomor);
              // Judul lengkap hanya sebagai baris sekunder bila beda dari label utama.
              const sub = r.label && r.judul && r.judul !== r.label ? r.judul : "";
              return (
                <li key={r.docId} className="flex gap-1.5 text-xs">
                  <span className="font-semibold text-blue-700">[{r.num}]</span>
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => setTarget({ docId: r.docId, page: r.pages[0] || 1 })}
                      className="text-left font-semibold text-slate-800 hover:text-blue-700 hover:underline"
                    >
                      {primary}
                    </button>
                    {sub && <div className="text-slate-500">{sub}</div>}
                    {r.pages.length > 0 && (
                      <div className="text-slate-400">hal. {formatPages(r.pages)}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {hover && (
        <div
          className="pointer-events-none fixed z-50 max-w-xs -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-3 py-2 text-xs text-white shadow-lg"
          style={{ left: hover.x, top: hover.y - 8 }}
        >
          <div className="font-semibold leading-snug">
            {primaryLabel(hover.label, hover.judul, hover.nomor)}
          </div>
          {hover.label && hover.judul && hover.judul !== hover.label && (
            <div className="text-slate-300">{hover.judul}</div>
          )}
          <div className="mt-0.5 text-slate-300">
            Hal. {hover.page} · klik untuk buka
          </div>
        </div>
      )}

      {target && (
        <CitationPreviewModal
          docId={target.docId}
          page={target.page}
          variant={variant}
          onClose={() => setTarget(null)}
        />
      )}
    </>
  );
}
